/**
 * Perfil da própria pessoa: nome, celular, e-mail (o "De" que sai nas
 * propostas que ela gera), troca de senha e tema. Sem gestão de outras
 * contas aqui — isso é `pages/Usuarios.tsx` (admin).
 */
import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useTheme, type ThemePreference } from '@/hooks/useTheme';
import { ApiError, patch, post } from '@/lib/api';
import type { User } from '@/lib/types';
import { maskPhone } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { Field } from '@/components/Field';
import { Button } from '@/components/Button';

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Escuro' },
  { value: 'system', label: 'Sistema' },
];

export function Perfil() {
  const { user, setUser } = useAuth();

  // Perfil só é renderizado dentro do layout autenticado, que já garante sessão.
  if (!user) return null;

  return (
    <>
      <PageHeader
        title="Meu perfil"
        meta='Este é o "De" que aparece nas propostas que você gerar — mantenha nome, celular e e-mail em dia.'
      />
      <div className="stack-lg">
        <DadosPessoaisPanel user={user} onSaved={setUser} />
        <SenhaPanel />
        <AparenciaPanel />
      </div>
    </>
  );
}

function DadosPessoaisPanel({ user, onSaved }: { user: User; onSaved: (user: User) => void }) {
  const toast = useToast();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const dirty = name !== user.name || email !== user.email || (phone || '') !== (user.phone ?? '');

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSaving(true);
    try {
      const res = await patch<{ data: User }>('/auth/me', {
        name,
        email,
        phone: phone || undefined,
      });
      onSaved(res.data);
      toast.success('Perfil atualizado.');
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setFormError('Não foi possível salvar agora. Tente de novo.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel title="Dados pessoais">
      <form className="stack" onSubmit={handleSubmit} noValidate>
        {formError ? (
          <p className="form-alert form-alert-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="field-grid">
          <Field
            label="Nome"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
          />
          <Field
            label="Celular"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(maskPhone(e.target.value))}
            error={fieldErrors.phone}
          />
        </div>
        <Field
          label="E-mail"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        <div className="row row-end">
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function SenhaPanel() {
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    if (newPassword.length < 8) {
      setFieldErrors({ newPassword: 'A nova senha precisa ter pelo menos 8 caracteres.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'As senhas não coincidem.' });
      return;
    }

    setSaving(true);
    try {
      await post('/auth/me/password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Senha alterada. Os outros dispositivos conectados foram desconectados.');
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setFormError('Não foi possível trocar a senha agora. Tente de novo.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel title="Senha">
      <form className="stack" onSubmit={handleSubmit} noValidate>
        <p className="muted">
          Trocar a senha encerra a sessão em todos os outros dispositivos — só este aqui continua
          conectado.
        </p>
        {formError ? (
          <p className="form-alert form-alert-error" role="alert">
            {formError}
          </p>
        ) : null}
        <Field
          label="Senha atual"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          error={fieldErrors.currentPassword}
        />
        <div className="field-grid">
          <Field
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            required
            hint="Mínimo de 8 caracteres."
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={fieldErrors.newPassword}
          />
          <Field
            label="Confirmar nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={fieldErrors.confirmPassword}
          />
        </div>
        <div className="row row-end">
          <Button type="submit" disabled={saving}>
            {saving ? 'Trocando…' : 'Trocar senha'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function AparenciaPanel() {
  const { theme, setTheme } = useTheme();
  return (
    <Panel title="Aparência">
      <div className="stack-sm">
        <p className="field-label">Tema</p>
        <div className="row row-wrap">
          {THEME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={theme === option.value}
              className={`btn btn-sm ${theme === option.value ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setTheme(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </Panel>
  );
}
